import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileBottomBar from '../../components/feature/MobileBottomBar';
import { useEffect, useState } from 'react';
import { useSEO } from '../../utils/seo';

const FAQsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How much do your removals services cost?',
      answer: 'Our pricing varies depending on the size of the move, distance, and specific requirements. We offer competitive rates with no hidden fees. Contact us with details of your move for a free, accurate quote. We\'ll provide a clear breakdown of costs upfront.',
    },
    {
      question: 'How do I get a quote?',
      answer: 'Getting a quote is easy. You can call us on 07530 166035, fill out our online quote form, or WhatsApp us. We\'ll need to know your moving from and to locations, the size of your property or items, and your preferred moving date. We\'ll respond within 24 hours with a detailed quote.',
    },
    {
      question: 'Are you insured?',
      answer: 'Yes, we\'re fully insured with comprehensive goods-in-transit and public liability insurance. This means your belongings are protected throughout the entire moving process. We take every precaution to ensure safe transportation, but insurance provides additional peace of mind.',
    },
    {
      question: 'What areas do you cover?',
      answer: 'We cover all of Lancashire including Accrington, Clayton-le-Moors, Blackburn, Burnley, Preston, Lancaster, Chorley, and surrounding areas. We also travel to neighbouring counties. If you\'re unsure whether we cover your area, please get in touch and we\'ll be happy to help.',
    },
    {
      question: 'Do you provide packing materials?',
      answer: 'Yes, we can supply professional-grade packing materials including boxes, bubble wrap, protective blankets, and tape. We also offer a full packing service where our team carefully packs your belongings for you. This can be included in your quote.',
    },
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking as early as possible, especially during busy periods like weekends and month-ends. However, we often have availability for short-notice moves. For single item moves, we can sometimes accommodate same-day or next-day requests. Contact us to check availability.',
    },
    {
      question: 'Can you move on weekends and evenings?',
      answer: 'Yes, we offer flexible scheduling including weekend and evening moves. This is particularly useful for office relocations where you want to minimise business disruption. Weekend and evening moves may be subject to different pricing – we\'ll discuss this when providing your quote.',
    },
    {
      question: 'What if I need to change my moving date?',
      answer: 'We understand that moving plans can change. Please contact us as soon as possible if you need to reschedule. We\'ll do our best to accommodate your new date. Our cancellation and rescheduling policy will be explained when you book.',
    },
    {
      question: 'Do you disassemble and reassemble furniture?',
      answer: 'Yes, we can disassemble larger furniture items like beds, wardrobes, and dining tables for safe transportation, and reassemble them at your new location. This service is included in most house removal quotes. Let us know what furniture you have when requesting a quote.',
    },
    {
      question: 'Can you move a single item or do I need a full removal?',
      answer: 'We absolutely do single item moves! Whether it\'s a sofa, washing machine, piano, or any other item, we can help. Our single item service is perfect for marketplace purchases, gifts, or when you just need one thing moved. Contact us for a quote.',
    },
    {
      question: 'What should I do to prepare for moving day?',
      answer: 'We recommend packing personal items in boxes, labelling them clearly, and ensuring pathways are clear. Disconnect appliances if possible. If you\'ve booked our packing service, we\'ll handle most of this for you. We\'ll provide a detailed preparation checklist when you book.',
    },
    {
      question: 'How long does a typical house move take?',
      answer: 'This depends on the size of your property and the distance to your new home. A typical 2-3 bedroom house move usually takes 4-8 hours including loading, transportation, and unloading. We\'ll give you an estimated timeframe when providing your quote.',
    },
    {
      question: 'Do you move office equipment and IT?',
      answer: 'Yes, we specialise in office relocations including careful handling of computers, servers, printers, and other IT equipment. We use anti-static packaging and secure transportation methods. We can also work outside business hours to minimise downtime.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept cash, bank transfer, and card payments. Payment terms will be discussed when you book. For larger moves, we may require a deposit with the balance due on completion. We\'ll provide a clear invoice with all costs itemised.',
    },
    {
      question: 'What happens if something gets damaged?',
      answer: 'While we take every precaution to prevent damage, if something does occur, we\'re fully insured. Report any damage to us immediately and we\'ll work with you to resolve it through our insurance. We have an excellent track record and handle all items with care.',
    },
  ];

  useSEO({
    title: 'FAQs | Removals Questions Answered | Dan & Van Lancashire',
    description: 'Frequently asked questions about our removals service. Pricing, insurance, areas covered, booking process & more. Get answers — 07530 166035',
    keywords: 'removals FAQs, moving questions, removals pricing, insurance questions, booking removals Lancashire',
    canonical: '/faqs',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <MobileBottomBar />

      {/* Hero Section */}
      <section className="relative bg-gray-50 py-24 mt-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-sans text-6xl font-bold text-charcoal mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-700">
              Everything you need to know about our removals service
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-sans text-xl font-bold text-charcoal pr-8">
                    {faq.question}
                  </span>
                  <i
                    className={`text-2xl text-gold transition-transform ${
                      openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </button>
                {openIndex === index && (
                  <div className="px-8 pb-6">
                    <div className="border-t border-gold/20 pt-6">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-sans text-4xl font-bold text-charcoal mb-6">
            Still have questions?
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            We're here to help. Get in touch and we'll answer any questions you have about your move.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:07530166035"
              className="px-8 py-4 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
            >
              Call 07530 166035
            </a>
            <Link
              to="/contact"
              className="px-8 py-4 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQsPage;
